import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import html2canvas from "html2canvas";

/**
 * Export selected dealers to a PDF report with:
 * - Map screenshot
 * - Summary statistics
 * - Per-dealer customer list
 */
export async function exportDealerReport({ mapElement, dealers, stats, uploadedName }) {
  const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 32;

  // Title bar
  doc.setFillColor(11, 15, 23);
  doc.rect(0, 0, pageWidth, 40, "F");
  doc.setTextColor(248, 250, 252);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("TürkBayi Harita - Bayi Bölge Raporu", margin, 26);
  doc.setFontSize(9);
  doc.setTextColor(148, 163, 184);
  doc.text(new Date().toLocaleString("tr-TR"), pageWidth - margin, 26, { align: "right" });

  // Map screenshot
  let y = 60;
  if (mapElement) {
    try {
      const canvas = await html2canvas(mapElement, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#070A0F",
        scale: 1.2,
        logging: false,
      });
      const img = canvas.toDataURL("image/jpeg", 0.75);
      const imgW = pageWidth - margin * 2;
      const imgH = (canvas.height / canvas.width) * imgW;
      const capped = Math.min(imgH, 360);
      doc.addImage(img, "JPEG", margin, y, imgW, capped);
      y += capped + 16;
    } catch (e) {
      console.error("Map capture failed", e);
    }
  }

  // Summary block
  doc.setFillColor(17, 23, 38);
  doc.rect(margin, y, pageWidth - margin * 2, 60, "F");
  doc.setTextColor(148, 163, 184);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const cellW = (pageWidth - margin * 2) / 5;
  const items = [
    ["MÜŞTERİ", (stats?.validCustomers ?? 0).toLocaleString("tr")],
    ["BAYİ", (stats?.totalDealers ?? 0).toLocaleString("tr")],
    ["İÇ NOKTA", (stats?.inliers ?? 0).toLocaleString("tr")],
    ["OUTLIER", (stats?.outliers ?? 0).toLocaleString("tr")],
    ["KAYNAK", uploadedName || "—"],
  ];
  items.forEach((it, i) => {
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(7);
    doc.text(it[0], margin + i * cellW + 8, y + 20);
    doc.setTextColor(248, 250, 252);
    doc.setFontSize(13);
    doc.text(String(it[1]), margin + i * cellW + 8, y + 44);
  });
  y += 76;

  // Dealer summary table
  const dealerTable = dealers.map(d => [
    d.name,
    d.inliers.toLocaleString("tr"),
    d.outliers.toLocaleString("tr"),
    d.count.toLocaleString("tr"),
    d.colorName || "",
  ]);
  autoTable(doc, {
    startY: y,
    head: [["Bayi Adı", "İç Nokta", "Outlier", "Toplam", "Renk"]],
    body: dealerTable,
    theme: "striped",
    styles: { fontSize: 8, cellPadding: 3, textColor: [51, 65, 85] },
    headStyles: { fillColor: [0, 51, 161], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [246, 248, 250] },
    margin: { left: margin, right: margin },
  });

  // Per-dealer customer list (new pages)
  for (const d of dealers) {
    if (!d.points || !d.points.length) continue;
    doc.addPage();
    doc.setFillColor(11, 15, 23);
    doc.rect(0, 0, pageWidth, 40, "F");
    doc.setFillColor(...hexToRgb(d.fill));
    doc.rect(0, 40, pageWidth, 4, "F");
    doc.setTextColor(248, 250, 252);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text(d.name, margin, 26);
    doc.setTextColor(148, 163, 184);
    doc.setFontSize(9);
    doc.text(`${d.inliers} müşteri  •  ${d.outliers} outlier  •  Merkez: ${d.centroid[1].toFixed(3)}°, ${d.centroid[0].toFixed(3)}°`, margin, 40 - 4);

    // cap list to 500 for PDF size
    const list = d.points.slice(0, 500).map(p => [
      p.sap,
      p.lat.toFixed(5),
      p.lng.toFixed(5),
      (p.dist ?? 0).toFixed(4) + "°",
    ]);
    autoTable(doc, {
      startY: 60,
      head: [["SAP Kodu", "Enlem", "Boylam", "Merkez Uzaklık"]],
      body: list,
      theme: "grid",
      styles: { fontSize: 8, cellPadding: 2.5 },
      headStyles: { fillColor: [19, 41, 75], textColor: 255, fontStyle: "bold" },
      margin: { left: margin, right: margin, top: 60 },
      didDrawPage: (data) => {
        if (data.pageNumber > 1) {
          doc.setFontSize(8);
          doc.setTextColor(148, 163, 184);
          doc.text(`${d.name} (devam)`, margin, 20);
        }
      }
    });
    if (d.points.length > 500) {
      const finalY = doc.lastAutoTable.finalY + 12;
      doc.setTextColor(148, 163, 184);
      doc.setFontSize(8);
      doc.text(`(İlk 500 kayıt gösterildi. Toplam: ${d.points.length})`, margin, finalY);
    }
  }

  const fileName = `bayi-bolge-raporu-${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
}

function hexToRgb(hex) {
  const s = hex.replace("#", "");
  return [parseInt(s.slice(0, 2), 16), parseInt(s.slice(2, 4), 16), parseInt(s.slice(4, 6), 16)];
}
